import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Pagination from '../pagination/Pagination';

const MemberMasterLogs = () => {
  const [logs, setLogs] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedDist, setSelectedDist] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Pagination states
  const [showPerPage, setShowPerPage] = useState(15);
  const [pagination, setPagination] = useState({
    start: 0,
    end: showPerPage,
  });

  const onChangePagination = (start, end) => {
    setPagination({ start, end });
  };

  // 1. Fetch approved districts for dropdown filter
  const fetchApprovedDistricts = async () => {
    try {
      const res = await axios.get('https://lionsdirectoryapi.emedha.in/auth/api/approved-districts');
      setDistricts(res.data || []);
    } catch (err) {
      console.error("Error fetching districts:", err);
    }
  };

  // 2. Fetch member logs with optional district filter
  const fetchLogs = async (dist = "ALL") => {
    try {
      const url = dist === "ALL"
        ? 'https://lionsdirectoryapi.emedha.in/auth/api/member-logs'
        : `https://lionsdirectoryapi.emedha.in/auth/api/member-logs?dist=${dist}`;

      const res = await axios.get(url);
      setLogs(res.data || []);
    } catch (err) {
      console.error("Error loading member logs:", err);
    }
  };

  useEffect(() => {
    fetchApprovedDistricts();
    fetchLogs(selectedDist);
  }, []);

  const handleDistrictChange = (e) => {
    const dist = e.target.value;
    setSelectedDist(dist);
    setPagination({ start: 0, end: showPerPage }); // Reset to page 1
    fetchLogs(dist);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination({ start: 0, end: showPerPage }); // Reset to page 1 on active search
  };

  const openDetails = (details) => {
    try {
      setSelectedDetails(JSON.parse(details));
    } catch (e) {
      setSelectedDetails(details);
    }
    setShowDetailModal(true);
  };

  // Filter logs by search term
  const filteredLogs = logs.filter((log) => {
    const term = searchTerm.toLowerCase();
    const action = log.action ? log.action.toLowerCase() : "";
    const performedBy = log.performedBy ? log.performedBy.toLowerCase() : "";
    const dist = log.dist ? log.dist.toLowerCase() : "";
    const membershipNo = log.membershipNo ? String(log.membershipNo).toLowerCase() : "";
    const memberName = log.memberName ? log.memberName.toLowerCase() : "";
    const clubName = log.clubName ? log.clubName.toLowerCase() : "";
    const details = log.details ? log.details.toLowerCase() : "";

    return (
      action.includes(term) ||
      performedBy.includes(term) ||
      dist.includes(term) ||
      membershipNo.includes(term) ||
      memberName.includes(term) ||
      clubName.includes(term) ||
      details.includes(term)
    );
  });

  const getActionBadge = (action) => {
    switch (action) {
      case 'ADD':
        return <span className="badge bg-success">ADD</span>;
      case 'UPDATE':
        return <span className="badge bg-primary">UPDATE</span>;
      case 'DELETE':
        return <span className="badge bg-danger">DELETE</span>;
      case 'BULK_UPLOAD':
        return <span className="badge bg-warning text-dark">BULK UPLOAD</span>;
      default:
        return <span className="badge bg-secondary">{action}</span>;
    }
  };

  return (
    <div className="container-fluid py-3">
      {/* Top Header & Search/Filter Controls */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 style={{ color: 'rgb(245, 170, 30)' }} className="m-0">
          Member Master Audit Logs
        </h4>

        <div className="d-flex gap-2 w-50">
          {/* District Dropdown Filter */}
          <select
            className="form-select w-50"
            value={selectedDist}
            onChange={handleDistrictChange}
          >
            <option value="ALL">All Approved Districts</option>
            {districts.map((d, idx) => (
              <option key={idx} value={d.district}>
                District {d.district}
              </option>
            ))}
          </select>

          {/* Search Input */}
          <input
            type="text"
            placeholder="Search by name, member No, club, user..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-control"
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th>#</th>
              <th>Action</th>
              <th>Performed By</th>
              <th>District</th>
              <th>Membership No</th>
              <th>Member Name</th>
              <th>Club Name</th>
              <th>Timestamp</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length > 0 ? (
              filteredLogs
                .slice(pagination.start, pagination.end)
                .map((log, idx) => (
                  <tr key={log.id || idx}>
                    <td>{pagination.start + idx + 1}</td>
                    <td>{getActionBadge(log.action)}</td>
                    <td className="fw-semibold">{log.performedBy}</td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        {log.dist || "N/A"}
                      </span>
                    </td>
                    <td>{log.membershipNo || "—"}</td>
                    <td>{log.memberName || "—"}</td>
                    <td>{log.clubName || "—"}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                      {new Date(log.actionTime).toLocaleString()}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={() => openDetails(log.details)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center text-muted py-3">
                  No member audit logs found matching the filter criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap">
        <span className="text-muted" style={{ fontSize: '14px' }}>
          Showing {filteredLogs.length > 0 ? pagination.start + 1 : 0} to{" "}
          {Math.min(pagination.end, filteredLogs.length)} of {filteredLogs.length} entries
        </span>

        <Pagination
          page={showPerPage}
          onChangePagination={onChangePagination}
          total={filteredLogs.length}
        />
      </div>

      {/* Details Preview Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Member Audit Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre
            style={{
              backgroundColor: '#f8f9fa',
              padding: '12px',
              borderRadius: '6px',
              maxHeight: '350px',
              overflowY: 'auto',
              fontSize: '13px'
            }}
          >
            {typeof selectedDetails === 'object'
              ? JSON.stringify(selectedDetails, null, 2)
              : selectedDetails || "No additional payload recorded."}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MemberMasterLogs;